import {latestReviewedClaim, resolveAllotmentCarryForward} from '../core/allotmentCarryForward'
import {FC, useMemo, useState, useEffect, useRef} from 'react'
import {toast, ToastContainer} from 'react-toastify'
import {KTSVG} from '../../../../_metronic/helpers'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import {showErrorToast} from '../../../shared/utils/apiErrorMessage'
import {useAuth} from '../../auth'
import {CREWING_READ_PERMISSIONS, useCrewingCanRead, useCrewingPermissionRevision} from '../core/crewingPermissionAccess'
import {useCrewingRoleAccess} from '../core/crewingRoleAccess'
import {canViewCrewFinancialDrafts} from '../core/crewFinancialDraftAccess'
import {hasFinancialDraftDelegation, canAccessFinancialDraft} from '../core/financialManagementDraftAccess'
import {Rank, Crew, Company, CompanyAdmin, Vessel} from '../core/_models'
import {
  getCrewClaims,
  createCrewClaim,
  updateCrewClaim,
  submitCrewClaim,
  saveCrewClaimMasterReview,
  getCrewOvertimes,
  reviewCrewClaim,
  rejectCrewClaim,
  uploadCrewClaimAttachment,
  deleteCrewClaimAttachment,
  deleteCrewClaim,
  CrewClaim,
  ClaimStatus,
  CreateCrewClaimRequest,
  UpdateCrewClaimRequest,
  getRanksforList,
  getCrewList,
  getCompanyList,
  getCompanyAdminList,
  getCrewLatestBankingDetails,
  getCrewAllotments,
  crewClaimAttachmentViewUrl,
  bulkMasterReviewCrewClaims,
  crewClaimAttachmentDownloadUrl,
} from '../core/_requests'
import CrewClaimModal from '../components/CrewClaimModal'
import ViewCrewClaimModal from '../components/ViewCrewClaimModal'
import { RemarksCell, RemarksModal } from '../../QHSE/components/StructuredRemarksView'
import CrewClaimReviewModal from '../components/CrewClaimReviewModal'
import CrewClaimAttachmentsModal from '../components/CrewClaimAttachmentsModal'
import {FileViewerModal} from '../../QHSE/components/FileViewerModal'
import CrewClaimExportModal from '../components/CrewClaimExportModal'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import CrewClaimMasterBulkReviewModal from '../components/CrewClaimMasterBulkReviewModal'
import CrewClaimMasterReviewModal from '../components/CrewClaimMasterReviewModal'
import {isClaimPendingMasterReview} from '../core/claimMasterReview'
import {getClaimRosterPeriod} from '../core/claimRosterPeriod'
import {
  buildCrewSignOnDateMap,
  getCrewSignOnMonth,
  toSignOnMonthKey,
} from '../core/signOnDateUtils'
import { getSignOnOffRecords, SignOnOffRecord } from '../core/_requests'
import {
  applyCrewingPresentationFilters,
  allowCompanyPresentationFilters,
  extractRows,
} from '../core/crewingPresentationFilters'
import { filterVesselsForPresentationScope } from '../../../shared/access/tenantScopeMatch'

type SortConfig = {
  key: keyof CrewClaim | 'total'
  direction: 'asc' | 'desc'
}

const normalizeMonthStart = (value?: string | null): string => {
  const raw = (value || '').trim()
  if (!raw) return ''
  const monthPart = raw.slice(0, 7)
  const [yearText, monthText] = monthPart.split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return ''
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`
}

const toMonthKey = (value?: string | null): string => {
  const normalized = normalizeMonthStart(value)
  return normalized ? normalized.slice(0, 7) : ''
}

const currentMonthStartLocal = (): string => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
}

const formatMonthLabel = (
  value?: string | null,
  monthStyle: 'short' | 'long' = 'short'
): string => {
  const normalized = normalizeMonthStart(value)
  if (!normalized) return '-'

  const [yearText, monthText] = normalized.slice(0, 7).split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  if (!Number.isFinite(year) || !Number.isFinite(month)) return '-'

  const utcDate = new Date(Date.UTC(year, month - 1, 1, 12, 0, 0))
  return utcDate.toLocaleDateString('en-IN', {month: monthStyle, year: 'numeric', timeZone: 'UTC'})
}

const previousMonthStart = (monthStart: string): string => {
  const normalized = normalizeMonthStart(monthStart)
  if (!normalized) return ''
  const [yearText, monthText] = normalized.slice(0, 7).split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  const prev = new Date(Date.UTC(year, month - 2, 1))
  return `${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}-01`
}

const isAllotmentOfficeReviewed = (row: {
  workflowStatus?: string | null
  reviewedAt?: string | null
}) => row.workflowStatus === 'REVIEWED' || Boolean(row.reviewedAt)

const isAllotmentReviewPending = (row: {
  workflowStatus?: string | null
  reviewedAt?: string | null
}) => row.workflowStatus === 'SUBMITTED' && !isAllotmentOfficeReviewed(row)

const isOvertimeOfficeReviewed = (row: {status?: string | null; reviewedAt?: string | null}) =>
  row.status === 'REVIEWED' || Boolean(row.reviewedAt)

const isOvertimeReviewPending = (row: {status?: string | null; reviewedAt?: string | null}) =>
  !isOvertimeOfficeReviewed(row) && row.status !== 'REJECTED'

const resolvePreviousMonthCarryForward = (
  allotments: Array<{
    statementMonth?: string | null
    carryForwardAmount?: number | null
    balanceBroughtForward?: number | null
    allotmentId?: number | null
    workflowStatus?: string | null
    reviewedAt?: string | null
  }>,
  claimMonth: string,
  claims?: Array<{
    crewId?: number | string | null
    claimMonth?: string | null
    balanceCf?: number | null
    bfAmount?: number | null
    wagesEarned?: number | null
    deductionsTotal?: number | null
    id?: number
    status?: string | null
    reviewedAt?: string | null
  }>,
  crewId?: number | string | null
): number => {
  const claimMonthStart = normalizeMonthStart(claimMonth)
  if (!claimMonthStart) return 0

  const prevMonth = previousMonthStart(claimMonthStart)
  if (!prevMonth) return 0

  const reviewed = allotments.filter((row) => isAllotmentOfficeReviewed(row))
  const pool = reviewed.length > 0 ? reviewed : allotments

  const normalized = pool
    .map((row) => {
      const rowMonthStart = normalizeMonthStart(row.statementMonth || undefined)
      const reviewedClaim = rowMonthStart && crewId != null
        ? latestReviewedClaim(claims || [], crewId, rowMonthStart.slice(0, 7)) : undefined
      const balance = resolveAllotmentCarryForward(row as any, reviewedClaim)
      return {
        id: Number(row.allotmentId) || 0,
        monthStart: rowMonthStart,
        balance,
      }
    })
    .filter((row) => row.monthStart)

  const exactPrev = normalized
    .filter((row) => row.monthStart === prevMonth)
    .sort((a, b) => b.id - a.id)[0]

  if (exactPrev) {
    return Number.isFinite(exactPrev.balance) ? exactPrev.balance : 0
  }

  const latestBefore = normalized
    .filter((row) => row.monthStart < claimMonthStart)
    .sort((a, b) => {
      if (a.monthStart === b.monthStart) return b.id - a.id
      return a.monthStart < b.monthStart ? 1 : -1
    })[0]

  return latestBefore && Number.isFinite(latestBefore.balance) ? latestBefore.balance : 0
}

// Claim routing still distinguishes master review from office review as a business workflow step.
const isMasterEquivalentRank = (rankId?: number | null, rankName?: string | null): boolean => {
  const normalizedName = (rankName || '').toLowerCase()
  return (
    Number(rankId) === 1 || normalizedName.includes('master') || normalizedName.includes('captain')
  )
}

const CrewClaims: FC = () => {
  const {currentUser, hasPermission, isSuperAdmin} = useAuth()
  const {
    isCrew,
    effectiveBehavior,
    isOfficeRole,
    isCompanyGroupAdmin,
    isCompanyAdmin: baseIsCompanyAdmin,
    isOperator,
  } = useCrewingRoleAccess()
  const roleId = currentUser?.role?.id
  const canManageOfficeDraft = hasFinancialDraftDelegation(effectiveBehavior, hasPermission('MANAGE_CREW_CLAIM_DRAFT'))
  const canViewDrafts = canViewCrewFinancialDrafts({isSuperAdmin, isOfficeRole}) || canManageOfficeDraft

  const canReadClaims = useCrewingCanRead(CREWING_READ_PERMISSIONS.CLAIM)
  const permissionRevision = useCrewingPermissionRevision()

  const operatorCompanyAdminId =
    Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? 0) ||
    undefined
  const isCompanyAdmin = baseIsCompanyAdmin || (isOperator && !!operatorCompanyAdminId) // SubCompany
  const crewRankName = (currentUser as any)?.rank?.rank ?? ''
  const crewRankId = Number((currentUser as any)?.rank?.id ?? 0)
  const isCrewMaster = isCrew && isMasterEquivalentRank(crewRankId, crewRankName)
  const currentCrewId = isCrew ? Number((currentUser as any)?.roleEntityId ?? 0) : 0
  const currentCrewUsername = currentUser?.username ?? ''
  const currentCrewName = (currentUser as any)?.roleEntityName ?? currentCrewUsername
  const currentUserId = Number((currentUser as any)?.id ?? 0)
  const currentUserUsername = String((currentUser as any)?.username ?? '').toLowerCase()
  const crewOfficeAlertStorageKey = `dismissedCrewClaimOfficeAlerts:${
    currentUserId || currentUserUsername || 'anon'
  }`
  const roleEntityId = Number((currentUser as any)?.roleEntityId ?? 0)

  const fixedCompanyGroupId = useMemo(() => {
    if (isCompanyGroupAdmin) {
      return (
        Number(
          (currentUser as any)?.companyGroupAdminId ??
            (currentUser as any)?.companyGroupAdmin?.id ??
            (roleEntityId || 0)
        ) || undefined
      )
    }
    if (isCompanyAdmin) {
      return (
        Number(
          (currentUser as any)?.companyGroupAdminId ??
            (currentUser as any)?.companyAdmin?.cga?.id ??
            0
        ) || undefined
      )
    }
    return undefined
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, roleEntityId])

  const fixedCompanyAdminId = useMemo(() => {
    if (!isCompanyAdmin) return undefined
    return (
      Number(
        (currentUser as any)?.companyAdminId ??
          (currentUser as any)?.companyAdmin?.id ??
          (roleEntityId || 0)
      ) || undefined
    )
  }, [currentUser, isCompanyAdmin, roleEntityId])

  const allowCompanyFilters = allowCompanyPresentationFilters(isSuperAdmin, roleId, isOperator)

  const showCompanyFilter = isSuperAdmin
  const showSubCompanyFilter = isSuperAdmin || isCompanyGroupAdmin
  const showVesselFilter = isSuperAdmin || isCompanyGroupAdmin || isCompanyAdmin || isOperator

  // Effective permissions (custom roles); superadmin bypass via auth helper
  const canCreate = isSuperAdmin || hasPermission('CREATE_CREW_CLAIM')
  const canReview = isSuperAdmin || (hasPermission('APPROVE_CREW_CLAIM') && isOfficeRole)
  const canEditClaims = isSuperAdmin || hasPermission('UPDATE_CREW_CLAIM')
  const canDeleteClaims = isSuperAdmin || hasPermission('DELETE_CREW_CLAIM')
  const canExportClaim =
    isSuperAdmin ||
    hasPermission('EXPORT_CREW_CLAIM') ||
    hasPermission('EXPORT_CREW_CLAIMS_AND_REIMBURSEMENTS') ||
    hasPermission('EXPORT_CLAIM') ||
    hasPermission('DOWNLOAD_CREW_CLAIM')
  const canDownloadClaim =
    isSuperAdmin ||
    hasPermission('DOWNLOAD_CREW_CLAIM') ||
    hasPermission('EXPORT_CREW_CLAIM') ||
    hasPermission('EXPORT_CREW_CLAIMS_AND_REIMBURSEMENTS') ||
    hasPermission('EXPORT_CLAIM') ||
    (isCrewMaster && !isCrew)
  const useCrewDropdownFilter = isCompanyGroupAdmin || isCompanyAdmin

  const [claims, setClaims] = useState<CrewClaim[]>([])
  const [ranks, setRanks] = useState<Rank[]>([])
  const [crewMembers, setCrewMembers] = useState<Crew[]>([])
  const [modalCrewMembers, setModalCrewMembers] = useState<Crew[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyAdminId, setSelectedCompanyAdminId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])

  useEffect(() => {
    if (globalState.vessel?.id) {
      setSelectedVesselId(Number(globalState.vessel.id))
    } else {
      setSelectedVesselId('')
    }

    if (globalState.company) {
      if (globalState.company.type === 'company_group') {
        setSelectedCompanyGroupId(Number(globalState.company.id))
        setSelectedCompanyAdminId('')
      } else {
        setSelectedCompanyAdminId(Number(globalState.company.id))
        setSelectedCompanyGroupId('')
      }
    } else {
      setSelectedCompanyGroupId('')
      setSelectedCompanyAdminId('')
    }
  }, [globalState.vessel, globalState.company])
  const [modalCompanyGroupId, setModalCompanyGroupId] = useState<number | ''>('')
  const [modalCompanyAdminId, setModalCompanyAdminId] = useState<number | ''>('')
  const [modalVesselId, setModalVesselId] = useState<number | ''>('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [currencyByCrewId, setCurrencyByCrewId] = useState<Record<number, string>>({})

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCrewId, setSelectedCrewId] = useState<number | ''>('')
  const [statusFilter, setStatusFilter] = useState<'All' | ClaimStatus>('All')
  const [selectedMonth, setSelectedMonth] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({key: 'crewName', direction: 'asc'})
  const [editingClaim, setEditingClaim] = useState<CrewClaim | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isNewClaim, setIsNewClaim] = useState(false)



  // Master bulk review modal state
  const [isMasterBulkReviewModalOpen, setIsMasterBulkReviewModalOpen] = useState(false)
  const [isMasterBulkReviewSaving, setIsMasterBulkReviewSaving] = useState(false)

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)

  // Crew Status filter state
  const [crewStatusFilter, setCrewStatusFilter] = useState<'ALL' | 'SIGNED_ON' | 'SIGNED_OFF'>('ALL')
  const [crewAssignmentStatus, setCrewAssignmentStatus] = useState<Record<number, string>>({})
  const crewFetchSeqRef = useRef(0)

  // View modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [viewClaim, setViewClaim] = useState<CrewClaim | null>(null)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [reviewingClaim, setReviewingClaim] = useState<CrewClaim | null>(null)
  const [masterReviewingClaim, setMasterReviewingClaim] = useState<CrewClaim | null>(null)
  const [isMasterReviewSaving, setIsMasterReviewSaving] = useState(false)
  const [isReviewSaving, setIsReviewSaving] = useState(false)
  const [dismissedOfficeAlertKeys, setDismissedOfficeAlertKeys] = useState<Set<string>>(new Set())

  // Attachments modal state
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false)
  const [attachmentsModalClaim, setAttachmentsModalClaim] = useState<CrewClaim | null>(null)

  // File viewer modal state
  const [fileModal, setFileModal] = useState<{
    visible: boolean
    title: string
    fileName?: string
    viewUrl?: string
    downloadUrl?: string
  }>({
    visible: false,
    title: '',
  })
  const [remarksModal, setRemarksModal] = useState<{
    visible: boolean
    title: string
    subtitle: string
    text: string
  }>({
    visible: false,
    title: '',
    subtitle: '',
    text: '',
  })

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const didInitFiltersRef = useRef(false)
  const modalCrewFetchSeqRef = useRef(0)

  // Fetch claims, ranks, and crew members on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(crewOfficeAlertStorageKey)
      setDismissedOfficeAlertKeys(stored ? new Set(JSON.parse(stored)) : new Set())
    } catch {
      setDismissedOfficeAlertKeys(new Set())
    }
  }, [crewOfficeAlertStorageKey])

  useEffect(() => {
    if (!roleId || !canReadClaims) return
    fetchClaims()
    fetchRanks()
    fetchCrewMembers()
    fetchSignOnOff()
    if (showSubCompanyFilter) {
      fetchCompanies()
    }
    if (showCompanyFilter) {
      fetchCompanyAdmins()
    }
    if (showVesselFilter) {
      fetchVessels()
    }
  }, [roleId, canCreate, showCompanyFilter, showSubCompanyFilter, showVesselFilter, canReadClaims, permissionRevision])

  useEffect(() => {
    if (didInitFiltersRef.current) return

    if (isCompanyGroupAdmin) {
      const cgaId = currentUser?.companyGroupAdminId ?? currentUser?.companyGroupAdmin?.id
      if (cgaId) {
        setSelectedCompanyGroupId(cgaId)
      }
    }

    if (isCompanyAdmin) {
      const caId = currentUser?.companyAdminId ?? currentUser?.companyAdmin?.id
      if (caId) {
        setSelectedCompanyAdminId(caId)
      }
    }

    if (isCrew) {
      const vesselId = currentUser?.vessel?.id
      if (vesselId) {
        setSelectedVesselId(vesselId)
      }
    }

    didInitFiltersRef.current = true
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, isCrew])

  const fetchClaims = async () => {
    setIsLoading(true)
    try {
      const data = extractRows<CrewClaim>(await getCrewClaims())
      setClaims(data)

      const uniqueCrewIds = Array.from(
        new Set(
          data
            .map((claim) => Number(claim.crewId))
            .filter((crewId) => Number.isFinite(crewId) && crewId > 0)
        )
      )

      if (uniqueCrewIds.length === 0) {
        setCurrencyByCrewId({})
      } else {
        const currencyRows = await Promise.all(
          uniqueCrewIds.map(async (crewId) => {
            const banking = await getCrewLatestBankingDetails(crewId)
            return {
              crewId,
              salaryCurrency: banking?.salaryCurrency,
              salaryOtherCurrency: banking?.salaryOtherCurrency,
            }
          })
        )

        const nextCurrencyByCrewId: Record<number, string> = {}
        currencyRows.forEach((row) => {
          const salaryCurrency = (row.salaryCurrency || '').toString().trim().toUpperCase()
          const salaryOtherCurrency = (row.salaryOtherCurrency || '')
            .toString()
            .trim()
            .toUpperCase()
          const resolvedCurrency = salaryCurrency === 'OTHER' ? salaryOtherCurrency : salaryCurrency

          if (resolvedCurrency) {
            nextCurrencyByCrewId[row.crewId] = resolvedCurrency
          }
        })
        setCurrencyByCrewId(nextCurrencyByCrewId)
      }
    } catch (error) {
      console.error('Error fetching claims:', error)
      showErrorToast(error, 'Failed to fetch claims', 'load')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchRanks = async () => {
    try {
      const data = await getRanksforList()
      setRanks(data)
    } catch (error) {
      console.error('Error fetching ranks:', error)
      showErrorToast(error, 'Failed to fetch ranks', 'load')
    }
  }

  const fetchCrewMembers = async (filters?: {
    companyGroupId?: number
    companyAdminId?: number
    vesselId?: number
  }) => {
    try {
      const requestSeq = ++crewFetchSeqRef.current
      const data = await getCrewList({...filters, period: selectedMonth || currentMonthStartLocal().slice(0, 7)})
      if (requestSeq !== crewFetchSeqRef.current) return
      setCrewMembers(data)
    } catch (error) {
      console.error('Error fetching crew members:', error)
      showErrorToast(error, 'Failed to fetch crew members', 'load')
    }
  }

  const modalClaimPeriod = getClaimRosterPeriod(editingClaim?.claimMonth, selectedMonth, currentMonthStartLocal())
  const modalSignOnDateByCrewId = useMemo(() => buildCrewSignOnDateMap(modalCrewMembers), [modalCrewMembers])

  const fetchModalCrewMembers = async (
    filters?: {
      companyGroupId?: number
      companyAdminId?: number
      vesselId?: number
    },
    requestSeq?: number
  ) => {
    try {
      const data = await getCrewList({...filters, period: modalClaimPeriod})
      if (requestSeq && requestSeq !== modalCrewFetchSeqRef.current) {
        return
      }
      setModalCrewMembers(data)
    } catch (error) {
      if (requestSeq && requestSeq !== modalCrewFetchSeqRef.current) {
        return
      }
      console.error('Error fetching crew members for modal:', error)
      showErrorToast(error, 'Failed to fetch crew members', 'load')
    }
  }

  const fetchCompanies = async () => {
    try {
      const data = await getCompanyList()
      setCompanies(data)
    } catch (error) {
      console.error('Error fetching companies:', error)
      showErrorToast(error, 'Failed to fetch companies', 'load')
    }
  }

  const fetchCompanyAdmins = async () => {
    try {
      const data = await getCompanyAdminList()
      setCompanyAdmins(data)
    } catch (error) {
      console.error('Error fetching sub-companies:', error)
      showErrorToast(error, 'Failed to fetch sub-companies', 'load')
    }
  }

  const fetchVessels = async () => {
    try {
      const data = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
      setVessels(data)
    } catch (error) {
      console.error('Error fetching vessels:', error)
      showErrorToast(error, 'Failed to fetch vessels', 'load')
    }
  }

  const fetchSignOnOff = async () => {
    try {
      const data = await getSignOnOffRecords()
      const records = extractRows<SignOnOffRecord>(data || [])
      const latestRecordByCrew = new Map<number, SignOnOffRecord>()
      for (const record of records) {
        const crewId = Number(record.crewId || 0)
        if (!crewId) continue
        const existing = latestRecordByCrew.get(crewId)
        if (!existing || record.id > existing.id) {
          latestRecordByCrew.set(crewId, record)
        }
      }
      const statusMap: Record<number, string> = {}
      latestRecordByCrew.forEach((record, crewId) => {
        statusMap[crewId] = record.status
      })
      setCrewAssignmentStatus(statusMap)
    } catch (error) {
      console.error('Error fetching sign on/off records:', error)
      showErrorToast(error, 'Failed to fetch sign on/off records', 'load')
    }
  }

  const handleDownloadClaimPDF = (claim: CrewClaim) => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
      const marginX = 50
      const marginY = 40

      const formatCurrency = (value: number) =>
        value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

      const toNumber = (value: unknown): number => {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : 0
      }

      const formatMonthLabel = (monthKey: string): string => {
        if (!monthKey) return '-'
        const [yearStr, monthStr] = monthKey.split('-')
        const year = Number(yearStr)
        const month = Number(monthStr)
        if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
          return monthKey
        }
        return new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
      }

      // Title
      doc.setFontSize(20)
      doc.setTextColor(28, 72, 126)
      doc.text('Crew Claim Details', marginX, marginY + 10)

      // Subtitle line with filter labels
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      const filterLabels: string[] = []
      if (claim.claimMonth) filterLabels.push(`Month: ${formatMonthLabel(claim.claimMonth.slice(0, 7))}`)
      if (claim.crewName) filterLabels.push(`Crew: ${claim.crewName}`)
      if (claim.vesselName) filterLabels.push(`Vessel: ${claim.vesselName}`)
      if (filterLabels.length > 0) {
        doc.text(filterLabels.join('  |  '), marginX, marginY + 32)
      }

      // Section 1: Personal & Claim Info
      const infoHeaders = ['Field', 'Value']
      const infoBody = [
        ['Crew Name', claim.crewName || '-'],
        ['Rank', claim.rankName || '-'],
        ['Vessel', claim.vesselName || '-'],
        ['Month', claim.claimMonth ? formatMonthLabel(claim.claimMonth.slice(0, 7)) : '-'],
        ['Status', claim.status || '-'],
      ]

      ;(doc as any).autoTable({
        startY: marginY + 55,
        head: [infoHeaders],
        body: infoBody,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255], fontStyle: 'bold' },
        bodyStyles: { textColor: [30, 30, 30] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: marginX, right: marginX },
        theme: 'grid',
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 120 },
          1: { cellWidth: 'auto' },
        },
      })

      // Section 2: Financial Details
      const lastTableEnd = (doc as any).lastAutoTable.finalY + 20

      doc.setFontSize(12)
      doc.setTextColor(28, 72, 126)
      doc.setFont('helvetica', 'bold')
      doc.text('Financial Breakdown', marginX, lastTableEnd)
      doc.setFont('helvetica', 'normal')

      const financialHeaders = ['Component', 'Amount']
      const financialBody = [
        ['Salary', formatCurrency(toNumber(claim.salary))],
        ['Medical', formatCurrency(toNumber(claim.medical))],
        ['Safety Gear', formatCurrency(toNumber(claim.safetyGear))],
        ['Travel', formatCurrency(toNumber(claim.travel))],
        ['Boarding & Lodging', formatCurrency(toNumber(claim.boardingLodging))],
        ['HRA Allowance', formatCurrency(toNumber(claim.hraAllowance))],
        ['Bonus', formatCurrency(toNumber(claim.bonus))],
        ['Misc Reimbursements', formatCurrency(toNumber(claim.miscReimbursements))],
        ['Overtime Amount', formatCurrency(toNumber(claim.overtimeAmount))],
      ]

      ;(doc as any).autoTable({
        startY: lastTableEnd + 14,
        head: [financialHeaders],
        body: financialBody,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255], fontStyle: 'bold' },
        bodyStyles: { textColor: [30, 30, 30] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: marginX, right: marginX },
        theme: 'grid',
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 160 },
          1: { cellWidth: 'auto', halign: 'right' },
        },
        foot: [['Total Amount', formatCurrency(toNumber(claim.totalAmount))]],
        footStyles: { fillColor: [230, 240, 250], textColor: [28, 72, 126], fontStyle: 'bold', fontSize: 10 },
      })

      // Section 3: Remarks
      const remarksEnd = (doc as any).lastAutoTable.finalY + 20

      doc.setFontSize(12)
      doc.setTextColor(28, 72, 126)
      doc.setFont('helvetica', 'bold')
      doc.text('Remarks', marginX, remarksEnd)
      doc.setFont('helvetica', 'normal')

      const remarksBody = [
        ['Crew Remarks', claim.remarks || '-'],
        ['Office Remarks', claim.officeRemarks || '-'],
      ]

      ;(doc as any).autoTable({
        startY: remarksEnd + 14,
        head: [['Type', 'Remarks']],
        body: remarksBody,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [28, 72, 126], textColor: [255, 255, 255], fontStyle: 'bold' },
        bodyStyles: { textColor: [30, 30, 30] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: marginX, right: marginX },
        theme: 'grid',
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 120 },
          1: { cellWidth: 'auto' },
        },
      })

      doc.save(`crew_claim_${claim.crewName}_${claim.claimMonth || 'all'}_${new Date().toISOString().split('T')[0]}.pdf`)
      toast.success('PDF downloaded successfully')
    } catch (error) {
      console.error('Error generating PDF:', error)
      showErrorToast(error, 'Failed to generate PDF', 'load')
    }
  }

  const calculateRowTotal = (claim: CrewClaim) =>
    (claim.medical || 0) +
    (claim.safetyGear || 0) +
    (claim.travel || 0) +
    (claim.boardingLodging || 0) +
    (claim.hraAllowance || 0) +
    (claim.bonus || 0) +
    (claim.miscReimbursements || 0) +
    (claim.overtimeAmount || 0)

  const resolveCurrencyCode = (
    salaryCurrency?: string | null,
    salaryOtherCurrency?: string | null
  ) => {
    const normalizedAccountCurrency = (salaryCurrency || '').toString().trim().toUpperCase()
    const normalizedOtherCurrency = (salaryOtherCurrency || '').toString().trim().toUpperCase()
    if (normalizedAccountCurrency === 'OTHER') return normalizedOtherCurrency || ''
    return normalizedAccountCurrency
  }

  const getClaimCurrency = (claim: CrewClaim) => {
    const mappedCurrency = currencyByCrewId[claim.crewId]
    if (mappedCurrency) return mappedCurrency
    return resolveCurrencyCode(claim.salaryCurrency, claim.salaryOtherCurrency)
  }

  const getCurrencySymbol = (currencyCode?: string) => {
    const code = (currencyCode || '').toString().trim().toUpperCase()
    switch (code) {
      case 'USD':
        return '$'
      case 'EUR':
        return '\u20AC'
      case 'GBP':
        return '\u00A3'
      case 'SGD':
        return 'S$'
      case 'INR':
        return '\u20B9'
      case 'AED':
        return '\u062F.\u0625 '
      default:
        try {
          const part = new Intl.NumberFormat('en', {
            style: 'currency',
            currency: code,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
            .formatToParts(0)
            .find((p) => p.type === 'currency')
          const maybeSymbol = (part?.value || '').trim()
          return maybeSymbol && maybeSymbol.toUpperCase() !== code ? maybeSymbol : ''
        } catch {
          return ''
        }
    }
  }

  const formatClaimAmount = (claim: CrewClaim, value: number) => {
    const symbol = getCurrencySymbol(getClaimCurrency(claim))
    return `${symbol}${formatCurrency(value)}`
  }

  const formatOptionalClaimAmount = (claim: CrewClaim, value?: number | null) => {
    if (value == null) return '-'
    return formatClaimAmount(claim, value)
  }

  const getCrewDeductionValue = (claim: CrewClaim): number | null => {
    if (claim.deductionsTotal != null) return claim.deductionsTotal

    const hasIndividualDeductions =
      claim.deductionCashAdvanceFromOffice != null ||
      claim.deductionBond != null ||
      claim.deductionOtherDeductions != null ||
      claim.deductionInternetCard != null ||
      claim.firstTimeDeduction != null

    if (!hasIndividualDeductions) return null

    return (
      (claim.deductionCashAdvanceFromOffice || 0) +
      (claim.deductionBond || 0) +
      (claim.deductionOtherDeductions || 0) +
      (claim.deductionInternetCard || 0) +
      (claim.firstTimeDeduction || 0)
    )
  }

  const getBalanceWagesValue = (claim: CrewClaim): number | null => {
    if (claim.balanceCf != null) return claim.balanceCf

    if (claim.bfAmount == null || claim.wagesEarned == null) return null

    const crewDeduction = getCrewDeductionValue(claim)
    return claim.bfAmount + claim.wagesEarned + calculateRowTotal(claim) - (crewDeduction || 0)
  }

  const formatCurrency = (value: number) =>
    value.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})

  const formatClaimMonthDisplay = (value?: string | null) => {
    return formatMonthLabel(value, 'short')
  }

  const handleTableHorizontalWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
    const container = event.currentTarget
    if (container.scrollWidth <= container.clientWidth) return

    // Keep vertical scrolling native; use Shift + wheel for horizontal intent.
    const isShiftHorizontalIntent =
      event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)
    if (!isShiftHorizontalIntent || event.deltaY === 0) return

    const isScrollingRight = event.deltaY > 0
    const canScrollContainerRight =
      container.scrollLeft + container.clientWidth < container.scrollWidth - 1
    const canScrollContainerLeft = container.scrollLeft > 0
    if (
      (isScrollingRight && !canScrollContainerRight) ||
      (!isScrollingRight && !canScrollContainerLeft)
    )
      return

    event.preventDefault()
    container.scrollLeft += event.deltaY
  }

  const handleSort = (key: keyof CrewClaim | 'total') => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const vesselById = useMemo(
    () => new Map(vessels.map((v) => [v.id, v])),
    [vessels]
  )

  const crewFilterOptions = useMemo(() => {
    const visibleCrew = canReadClaims ? crewMembers : []
    const uniqueById = new Map<number, string>()

    applyCrewingPresentationFilters(visibleCrew, {
      selectedVesselId,
      selectedCrewId,
      allowCompanyPresentationFilters: allowCompanyFilters,
      selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
      selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
    }).forEach((crew) => {
      const crewId = Number(crew.id)
      if (Number.isFinite(crewId) && crewId > 0) {
        uniqueById.set(crewId, String(crew.name || `Crew ${crewId}`))
      }
    })

    if (uniqueById.size === 0 && canReadClaims) {
      applyCrewingPresentationFilters(claims, {
        selectedVesselId,
        allowCompanyPresentationFilters: allowCompanyFilters,
        selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
        selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
      }).forEach((claim) => {
        const crewId = Number(claim.crewId)
        if (!uniqueById.has(crewId)) {
          uniqueById.set(crewId, String(claim.crewName || `Crew ${crewId}`))
        }
      })
    }

    return Array.from(uniqueById.entries())
      .map(([id, name]) => ({id, name}))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [
    crewMembers,
    claims,
    selectedCompanyGroupId,
    selectedCompanyAdminId,
    selectedVesselId,
    selectedCrewId,
    allowCompanyFilters,
    canReadClaims,
  ])

  useEffect(() => {
    if (!selectedCrewId) return
    const exists = crewFilterOptions.some((crew) => crew.id === Number(selectedCrewId))
    if (!exists) setSelectedCrewId('')
  }, [selectedCrewId, crewFilterOptions])

  const crewSignOnDateByCrewId = useMemo(() => buildCrewSignOnDateMap(crewMembers), [crewMembers])

  // The API validates historical months against all service periods.
  const handleSelectedMonthChange = (value: string) => {
    setSelectedMonth(value)
  }

  const filteredClaims = useMemo(() => {
    const visible = canReadClaims ? claims : []
    return applyCrewingPresentationFilters(visible, {
      searchTerm: useCrewDropdownFilter ? undefined : searchTerm,
      selectedCrewId: useCrewDropdownFilter ? selectedCrewId : undefined,
      selectedVesselId,
      allowCompanyPresentationFilters: allowCompanyFilters,
      selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
      selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
    }, {
      vesselById,
      extraPredicate: (claim) => {
        const typed = claim
        const isPendingMasterReview =
          !isMasterEquivalentRank(typed.rankId, typed.rankName) &&
          (typed.status === 'MASTER_REVIEW_PENDING' ||
            (typed.status === 'DRAFT' && !!typed.submittedAt))
        const isDraftVisibleForRole = typed.status !== 'DRAFT' || canAccessFinancialDraft({
          isSuperAdmin, isOfficeRole, hasDelegation: canManageOfficeDraft, rowEligible: typed.canManageFinancialDraft})
        const term = searchTerm.trim().toLowerCase()
        const matchesCrew = !selectedCrewId || typed.crewId === Number(selectedCrewId)
        const claimMonthKey = toMonthKey(typed.claimMonth)
        const matchesSearch =
          (typed.crewName || '').toLowerCase().includes(term) ||
          (typed.rankName || '').toLowerCase().includes(term)
        const matchesStatus =
          statusFilter === 'All' ||
          (statusFilter === 'MASTER_REVIEW_PENDING' && isPendingMasterReview) ||
          (statusFilter === 'DRAFT' && typed.status === 'DRAFT' && !isPendingMasterReview) ||
          (statusFilter !== 'MASTER_REVIEW_PENDING' &&
            statusFilter !== 'DRAFT' &&
            typed.status === statusFilter)
        const matchesMonth = !selectedMonth || claimMonthKey === selectedMonth
        const matchesCrewStatus = crewStatusFilter === 'ALL' || crewAssignmentStatus[typed.crewId] === crewStatusFilter
        return (
          isDraftVisibleForRole &&
          (useCrewDropdownFilter ? matchesCrew : matchesSearch) &&
          matchesStatus &&
          matchesMonth &&
          matchesCrewStatus
        )
      },
    })
  }, [
    claims,
    canReadClaims,
    searchTerm,
    selectedCrewId,
    statusFilter,
    selectedMonth,
    selectedCompanyGroupId,
    selectedCompanyAdminId,
    selectedVesselId,
    vesselById,
    canViewDrafts,
    isOfficeRole,
    isSuperAdmin,
    canManageOfficeDraft,
    useCrewDropdownFilter,
    allowCompanyFilters,
    crewSignOnDateByCrewId,
    crewStatusFilter,
    crewAssignmentStatus,
  ])

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [
    searchTerm,
    selectedCrewId,
    statusFilter,
    selectedMonth,
    selectedCompanyGroupId,
    selectedCompanyAdminId,
    selectedVesselId,
    crewStatusFilter,
  ])

  useEffect(() => {
    if (!canReadClaims) return

    fetchCrewMembers({
      companyGroupId: selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined,
      companyAdminId: selectedCompanyAdminId ? Number(selectedCompanyAdminId) : undefined,
      vesselId: selectedVesselId ? Number(selectedVesselId) : undefined,
    })
  }, [isSuperAdmin, canCreate, canReadClaims, selectedMonth, selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId])

  useEffect(() => {
    if (!(isSuperAdmin || canManageOfficeDraft) || !canCreate || !isModalOpen || !isNewClaim) return

    modalCrewFetchSeqRef.current += 1
    const requestSeq = modalCrewFetchSeqRef.current
    setModalCrewMembers([])

    fetchModalCrewMembers(
      {
        companyGroupId: fixedCompanyGroupId ?? (modalCompanyGroupId ? Number(modalCompanyGroupId) : undefined),
        companyAdminId: fixedCompanyAdminId ?? (modalCompanyAdminId ? Number(modalCompanyAdminId) : undefined),
        vesselId: modalVesselId ? Number(modalVesselId) : undefined,
      },
      requestSeq
    )
  }, [
    isSuperAdmin,
    canManageOfficeDraft,
    canCreate,
    isModalOpen,
    isNewClaim,
    modalCompanyGroupId,
    modalCompanyAdminId,
    modalVesselId,
    modalClaimPeriod,
    fixedCompanyGroupId,
    fixedCompanyAdminId,
  ])

  const effectiveCompanyGroupId =
    fixedCompanyGroupId ?? (selectedCompanyGroupId ? Number(selectedCompanyGroupId) : undefined)
  const effectiveCompanyAdminId =
    fixedCompanyAdminId ?? (selectedCompanyAdminId ? Number(selectedCompanyAdminId) : undefined)

  const filteredCompanyAdmins = useMemo(() => {
    if (!effectiveCompanyGroupId) return companies
    return companies.filter((ca) => {
      const parentCompanyId =
        (ca as any)?.cga?.id ??
        (ca as any)?.cgaid?.id ??
        (ca as any)?.companyGroupAdmin?.id ??
        (ca as any)?.companyGroupAdminId
      return parentCompanyId === effectiveCompanyGroupId
    })
  }, [companies, effectiveCompanyGroupId])

  const filteredModalCompanyAdmins = useMemo(() => {
    if (!modalCompanyGroupId) return companies
    return companies.filter((ca) => {
      const parentCompanyId =
        (ca as any)?.cga?.id ??
        (ca as any)?.cgaid?.id ??
        (ca as any)?.companyGroupAdmin?.id ??
        (ca as any)?.companyGroupAdminId
      return parentCompanyId === Number(modalCompanyGroupId)
    })
  }, [companies, modalCompanyGroupId])

  useEffect(() => {
    if (!modalCompanyAdminId) return
    const exists = filteredModalCompanyAdmins.some((ca) => ca.id === Number(modalCompanyAdminId))
    if (!exists) {
      setModalCompanyAdminId('')
      setModalVesselId('')
    }
  }, [modalCompanyAdminId, filteredModalCompanyAdmins])

  const shouldShowSubCompanyFilter =
    showSubCompanyFilter &&
    (isCompanyGroupAdmin
      ? filteredCompanyAdmins.length > 0
      : !!effectiveCompanyGroupId && filteredCompanyAdmins.length > 0)

  useEffect(() => {
    if (!selectedCompanyAdminId) return
    const exists = filteredCompanyAdmins.some((ca) => ca.id === Number(selectedCompanyAdminId))
    if (!exists) {
      setSelectedCompanyAdminId('')
      setSelectedVesselId('')
    }
  }, [selectedCompanyAdminId, filteredCompanyAdmins])

  const filteredVessels = useMemo(() => {
    if (!allowCompanyFilters) return vessels
    return filterVesselsForPresentationScope(vessels, {
      companyAdminId: effectiveCompanyAdminId,
      companyGroupId: effectiveCompanyGroupId,
    })
  }, [vessels, effectiveCompanyAdminId, effectiveCompanyGroupId, allowCompanyFilters])

  useEffect(() => {
    if (!selectedVesselId) return
    const exists = filteredVessels.some((v) => v.id === Number(selectedVesselId))
    if (!exists) setSelectedVesselId('')
  }, [selectedVesselId, filteredVessels])

  const selfCrewMember = useMemo(() => {
    if (!isCrew) return undefined
    if (currentCrewId) {
      const byId = crewMembers.find((member) => member.id === currentCrewId)
      if (byId) return byId
    }
    if (!currentCrewUsername) return undefined
    return crewMembers.find((member) => member.username === currentCrewUsername)
  }, [isCrew, currentCrewId, currentCrewUsername, crewMembers])

  const selectableCrewMembers = useMemo(() => {
    if (!isCrew || isCrewMaster) return crewMembers
    return selfCrewMember ? [selfCrewMember] : []
  }, [isCrew, isCrewMaster, crewMembers, selfCrewMember])

  const getOfficeAlertKey = (claim: CrewClaim) => {
    const ts = claim.updatedAt || claim.reviewedAt || claim.submittedAt || ''
    return `${claim.id}:${ts}`
  }

  const crewOfficeUpdateAlerts = useMemo(() => {
    if (!isCrew || isCrewMaster) return []

    return claims.filter((claim) => {
      if (!claim?.id) return false
      if (dismissedOfficeAlertKeys.has(getOfficeAlertKey(claim))) return false

      const matchesCrew =
        currentCrewId > 0
          ? claim.crewId === currentCrewId
          : (claim.crewName || '').trim().toLowerCase() ===
            (currentCrewName || '').trim().toLowerCase()
      if (!matchesCrew) return false

      if (
        claim.status !== 'SUBMITTED' &&
        claim.status !== 'REVIEWED' &&
        claim.status !== 'REJECTED'
      )
        return false

      const officeRemarks = (claim.officeRemarks || '').trim()
      const submittedAtMs = Date.parse(claim.submittedAt || '')
      const updatedAtMs = Date.parse(claim.updatedAt || '')
      const officeTouchedByUpdate =
        Number.isFinite(submittedAtMs) &&
        Number.isFinite(updatedAtMs) &&
        updatedAtMs > submittedAtMs + 1000

      return !!officeRemarks || officeTouchedByUpdate
    })
  }, [isCrew, isCrewMaster, claims, dismissedOfficeAlertKeys, currentCrewId, currentCrewName])

  const sortedClaims = useMemo(() => {
    const copy = [...filteredClaims]
    copy.sort((a, b) => {
      const getValue = (claim: CrewClaim) =>
        sortConfig.key === 'total'
          ? calculateRowTotal(claim)
          : claim[sortConfig.key as keyof CrewClaim]
      const first = getValue(a)
      const second = getValue(b)
      if (typeof first === 'number' && typeof second === 'number') {
        return sortConfig.direction === 'asc' ? first - second : second - first
      }
      return sortConfig.direction === 'asc'
        ? String(first || '').localeCompare(String(second || ''))
        : String(second || '').localeCompare(String(first || ''))
    })
    return copy
  }, [filteredClaims, sortConfig])

  const pendingMasterReviewRecords = useMemo(() => {
    return sortedClaims.filter((claim) => {
      const isPendingMasterReview =
        !isMasterEquivalentRank(claim.rankId, claim.rankName) &&
        (claim.status === 'MASTER_REVIEW_PENDING' ||
          (claim.status === 'DRAFT' && !!claim.submittedAt))
      return isPendingMasterReview
    })
  }, [sortedClaims])

  // Pagination calculations
  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = sortedClaims.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.ceil(sortedClaims.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const totals = useMemo(() => {
    const totalAmount = filteredClaims.reduce((sum, claim) => sum + calculateRowTotal(claim), 0)
    const submitted = filteredClaims.filter((claim) => claim.status === 'SUBMITTED').length
    const reviewed = filteredClaims.filter((claim) => claim.status === 'REVIEWED').length
    return {
      totalAmount,
      submitted,
      reviewed,
      totalClaims: filteredClaims.length,
    }
  }, [filteredClaims])

  const firstClaimIdByCrew = useMemo(() => {
    const byCrew = new Map<number, CrewClaim[]>()

    claims.forEach((row) => {
      const crewId = Number(row.crewId || 0)
      if (!crewId || !row?.id) return
      const existing = byCrew.get(crewId) || []
      existing.push(row)
      byCrew.set(crewId, existing)
    })

    const toSortableTime = (value?: string | null) => {
      const timestamp = Date.parse(value || '')
      return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER
    }

    const firstByCrew = new Map<number, number>()
    byCrew.forEach((rows, crewId) => {
      const first = [...rows].sort((firstRow, secondRow) => {
        const firstMonth = toSortableTime(firstRow.claimMonth)
        const secondMonth = toSortableTime(secondRow.claimMonth)
        if (firstMonth !== secondMonth) return firstMonth - secondMonth

        const firstCreated = toSortableTime(firstRow.createdAt)
        const secondCreated = toSortableTime(secondRow.createdAt)
        if (firstCreated !== secondCreated) return firstCreated - secondCreated

        return Number(firstRow.id || 0) - Number(secondRow.id || 0)
      })[0]

      if (first?.id) {
        firstByCrew.set(crewId, Number(first.id))
      }
    })

    return firstByCrew
  }, [claims])

  const hasExistingClaimForCurrentNonMasterCrew = useMemo(() => {
    if (!isCrew || isCrewMaster || !currentCrewId) return false
    return claims.some((row) => Number(row.crewId || 0) === Number(currentCrewId))
  }, [isCrew, isCrewMaster, currentCrewId, claims])

  const showOneTimeClaimFields = useMemo(() => {
    if (!editingClaim) return true

    const shouldApplyOneTimeRule = isCrewMaster || canReview
    if (!shouldApplyOneTimeRule) return true

    const crewId = Number(editingClaim.crewId || 0)
    if (!crewId) return true

    const crewClaims = claims.filter((row) => Number(row.crewId) === crewId)
    if (crewClaims.length === 0) return true

    const toSortableTime = (value?: string | null) => {
      const timestamp = Date.parse(value || '')
      return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER
    }

    const sorted = [...crewClaims].sort((first, second) => {
      const firstMonth = toSortableTime(first.claimMonth)
      const secondMonth = toSortableTime(second.claimMonth)
      if (firstMonth !== secondMonth) return firstMonth - secondMonth

      const firstCreated = toSortableTime(first.createdAt)
      const secondCreated = toSortableTime(second.createdAt)
      if (firstCreated !== secondCreated) return firstCreated - secondCreated

      return Number(first.id || 0) - Number(second.id || 0)
    })

    const firstClaim = sorted[0]
    if (!firstClaim) return true
    if (isNewClaim) return false

    return Number(firstClaim.id || 0) === Number(editingClaim.id || 0)
  }, [editingClaim, isCrewMaster, canReview, claims, isNewClaim])

  const hasExistingClaimForSelectedMonth = useMemo(() => {
    if (!editingClaim) return false

    const crewId = Number(editingClaim.crewId || 0)
    const monthKey = (editingClaim.claimMonth || '').slice(0, 7)
    if (!crewId || !monthKey) return false

    return claims.some((row) => {
      const sameCrew = Number(row.crewId || 0) === crewId
      const sameMonth = (row.claimMonth || '').slice(0, 7) === monthKey
      if (!sameCrew || !sameMonth) return false

      if (isNewClaim) return true
      return Number(row.id || 0) !== Number(editingClaim.id || 0)
    })
  }, [editingClaim, claims, isNewClaim])

  const handleOpenModal = (claim: CrewClaim, isNew = false) => {
    setEditingClaim({...claim})
    setIsNewClaim(isNew)
    setIsModalOpen(true)
    if (isNew) {
      modalCrewFetchSeqRef.current += 1
      setModalCompanyGroupId('')
      setModalCompanyAdminId('')
      setModalVesselId('')
      setModalCrewMembers([])
    }
  }

  const handleCloseModal = () => {
    modalCrewFetchSeqRef.current += 1
    setIsModalOpen(false)
    setEditingClaim(null)
    setIsNewClaim(false)
    setModalCompanyGroupId('')
    setModalCompanyAdminId('')
    setModalVesselId('')
    setModalCrewMembers([])
  }

  const ensureOvertimeReviewCompleted = async (
    crewId: number,
    claimMonth?: string | null,
    actionLabel: string = 'save this claim'
  ) => {
    const monthKey = toMonthKey(claimMonth)
    if (!crewId || !monthKey) {
      toast.error('Please select crew and claim month first.')
      return false
    }

    const periodLabel = formatMonthLabel(claimMonth || `${monthKey}-01`, 'short')

    try {
      const overtimes = await getCrewOvertimes()
      const matchingOvertimes = overtimes.filter((row) => {
        const sameCrew = Number(row.crewId || 0) === Number(crewId)
        const sameMonth = toMonthKey(row.overtimeMonth) === monthKey
        return sameCrew && sameMonth
      })

      if (
        matchingOvertimes.length === 0 ||
        matchingOvertimes.some((row) => isOvertimeOfficeReviewed(row)) ||
        !matchingOvertimes.some((row) => isOvertimeReviewPending(row))
      ) {
        return true
      }

      toast.error(`Complete OT office review for ${periodLabel} before you ${actionLabel}.`)
      return false
    } catch (error) {
      console.error('Error validating overtime review status:', error)
      showErrorToast(error, 'Unable to validate OT review status right now.', 'load')
      return false
    }
  }

  const ensurePreviousAllotmentReviewCompleted = async (
    crewId: number,
    claimMonth?: string | null,
    actionLabel: string = 'create this claim'
  ) => {
    const claimMonthStart = normalizeMonthStart(claimMonth)
    if (!crewId || !claimMonthStart) {
      return true
    }

    const prevMonth = previousMonthStart(claimMonthStart)
    if (!prevMonth) return true

    try {
      const allotments = await getCrewAllotments(crewId).catch(() => [])
      const latestPrevAllotment = allotments
        .filter((row) => normalizeMonthStart(row.statementMonth || undefined) === prevMonth)
        .sort((a, b) => (Number(b.allotmentId) || 0) - (Number(a.allotmentId) || 0))[0]

      if (!latestPrevAllotment || !isAllotmentReviewPending(latestPrevAllotment)) {
        return true
      }

      toast.error(
        `Complete allotment office review for ${formatMonthLabel(
          prevMonth,
          'short'
        )} before you ${actionLabel}.`
      )
      return false
    } catch (error) {
      console.error('Error validating previous allotment review status:', error)
      showErrorToast(error, 'Unable to validate allotment review status right now.', 'load')
      return false
    }
  }

  const handleSaveClaim = async (pendingFiles?: File[]) => {
    if (!editingClaim) return
    if (isOfficeRole && !isSuperAdmin && (isNewClaim || editingClaim.status === 'DRAFT') && !editingClaim.vesselId) {
      toast.error('Please select a vessel')
      return
    }

    if (
      isNewClaim &&
      isCrew &&
      !isCrewMaster &&
      Number(editingClaim.crewId || 0) === Number(currentCrewId)
    ) {
      const hasExisting = claims.some((row) => Number(row.crewId || 0) === Number(currentCrewId))
      if (hasExisting) {
        toast.error(
          'Only first claim can be created by crew. From 2nd month, Crew Master must create it.'
        )
        return
      }
    }

    // Validate required fields for new claims
    if (isNewClaim) {
      if (!editingClaim.crewId || editingClaim.crewId === 0) {
        toast.error('Please select a crew member')
        return
      }
      if (!editingClaim.rankId) {
        toast.error('Please select a rank')
        return
      }
    }

    const canProceedWithClaim = await ensureOvertimeReviewCompleted(
      Number(editingClaim.crewId || 0),
      editingClaim.claimMonth,
      isNewClaim ? 'create this claim' : 'save this claim'
    )
    if (!canProceedWithClaim) return

    const isPreviousAllotmentReviewed = await ensurePreviousAllotmentReviewCompleted(
      Number(editingClaim.crewId || 0),
      editingClaim.claimMonth,
      isNewClaim ? 'create this claim' : 'save this claim'
    )
    if (!isPreviousAllotmentReviewed) return

    setIsSaving(true)

    try {
      if (isNewClaim) {
        const request: CreateCrewClaimRequest = {
          crewId: editingClaim.crewId,
          vesselId: editingClaim.vesselId || undefined,
          rankId: editingClaim.rankId || undefined,
          claimMonth: editingClaim.claimMonth,
          salary: editingClaim.salary,
          medical: editingClaim.medical,
          safetyGear: editingClaim.safetyGear,
          travel: editingClaim.travel,
          boardingLodging: editingClaim.boardingLodging,
          hraAllowance: editingClaim.hraAllowance,
          bonus: editingClaim.bonus,
          miscReimbursements: editingClaim.miscReimbursements,
          overtimeAmount: editingClaim.overtimeAmount ?? 0,
          bfAmount: editingClaim.bfAmount || 0,
          wagesEarned: editingClaim.wagesEarned || 0,
          allotmentAmount: editingClaim.allotmentAmount || 0,
          cashAdvance: editingClaim.cashAdvance || 0,
          balanceCf: editingClaim.balanceCf || 0,
          unionSocialDues: editingClaim.unionSocialDues || 'As per CBA',
          deductionCashAdvanceFromOffice: editingClaim.deductionCashAdvanceFromOffice || 0,
          deductionOtherDeductions: editingClaim.deductionOtherDeductions || 0,
          deductionInternetCard: editingClaim.deductionInternetCard || 0,
          deductionsTotal: editingClaim.deductionsTotal || 0,
          remarks: editingClaim.remarks,
        }
        const createdClaim = await createCrewClaim(request)

        // Upload pending attachments if any
        if (pendingFiles && pendingFiles.length > 0 && createdClaim.id) {
          for (const file of pendingFiles) {
            try {
              await uploadCrewClaimAttachment(createdClaim.id, file)
            } catch (uploadError) {
              console.error('Error uploading attachment:', uploadError)
              showErrorToast(uploadError, `Failed to upload ${file.name}`, 'save')
            }
          }
        }

        toast.success('Claim created successfully')
      } else {
        const request: UpdateCrewClaimRequest = {
          ...(isOfficeRole && !isSuperAdmin && editingClaim.status === 'DRAFT' && !editingClaim.submittedAt
            ? {vesselId: editingClaim.vesselId || undefined} : {}),
          rankId: editingClaim.rankId || undefined,
          claimMonth: editingClaim.claimMonth,
          salary: editingClaim.salary,
          medical: editingClaim.medical,
          safetyGear: editingClaim.safetyGear,
          travel: editingClaim.travel,
          boardingLodging: editingClaim.boardingLodging,
          hraAllowance: editingClaim.hraAllowance,
          bonus: editingClaim.bonus,
          miscReimbursements: editingClaim.miscReimbursements,
          overtimeAmount: editingClaim.overtimeAmount ?? 0,
          bfAmount: editingClaim.bfAmount || 0,
          wagesEarned: editingClaim.wagesEarned || 0,
          allotmentAmount: editingClaim.allotmentAmount || 0,
          cashAdvance: editingClaim.cashAdvance || 0,
          balanceCf: editingClaim.balanceCf || 0,
          unionSocialDues: editingClaim.unionSocialDues || 'As per CBA',
          deductionCashAdvanceFromOffice: editingClaim.deductionCashAdvanceFromOffice || 0,
          deductionOtherDeductions: editingClaim.deductionOtherDeductions || 0,
          deductionInternetCard: editingClaim.deductionInternetCard || 0,
          deductionsTotal: editingClaim.deductionsTotal || 0,
          remarks: editingClaim.remarks,
        }
        await updateCrewClaim(editingClaim.id, request)
        toast.success('Claim updated successfully')
      }
      await fetchClaims()
      handleCloseModal()
    } catch (error) {
      console.error('Error saving claim:', error)
      showErrorToast(error, 'Failed to save claim', isNewClaim ? 'create' : 'update')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmitClaim = async (claim: CrewClaim) => {
    try {
      const crewId = Number(claim.crewId || 0)
      const canSubmit = await ensureOvertimeReviewCompleted(
        crewId,
        claim.claimMonth,
        'submit this claim'
      )
      if (!canSubmit) return

      const canSubmitAgainstAllotment = await ensurePreviousAllotmentReviewCompleted(
        crewId,
        claim.claimMonth,
        'submit this claim'
      )
      if (!canSubmitAgainstAllotment) return

      const isFirstClaimForCrew =
        crewId > 0 && Number(firstClaimIdByCrew.get(crewId) || 0) === Number(claim.id || 0)
      const wasPendingMasterReview =
        !isMasterEquivalentRank(claim.rankId, claim.rankName) &&
        (claim.status === 'MASTER_REVIEW_PENDING' ||
          (claim.status === 'DRAFT' && !!claim.submittedAt))
      let submitted = await submitCrewClaim(claim.id)
      let isPendingMasterReviewNow =
        !isMasterEquivalentRank(submitted.rankId, submitted.rankName) &&
        (submitted.status === 'MASTER_REVIEW_PENDING' ||
          (submitted.status === 'DRAFT' && !!submitted.submittedAt))

      // For 2nd+ claims filled by Crew Master, skip separate master-review cycle and auto-forward to office.
      if (isCrewMaster && !isFirstClaimForCrew && isPendingMasterReviewNow) {
        submitted = await submitCrewClaim(claim.id)
        isPendingMasterReviewNow =
          !isMasterEquivalentRank(submitted.rankId, submitted.rankName) &&
          (submitted.status === 'MASTER_REVIEW_PENDING' ||
            (submitted.status === 'DRAFT' && !!submitted.submittedAt))
      }

      if (isPendingMasterReviewNow) {
        toast.success('Claim submitted to master for review')
      } else if (submitted.status === 'SUBMITTED' && (wasPendingMasterReview || isCrewMaster)) {
        toast.success('Claim submitted for office review')
      } else {
        toast.success('Claim submitted for office review')
      }
      await fetchClaims()
    } catch (error) {
      console.error('Error submitting claim:', error)
      showErrorToast(error, 'Failed to submit claim', 'save')
    }
  }

  const handleMasterReviewClaim = async (masterRemarks: string, approve: boolean) => {
    if (!masterReviewingClaim || isMasterReviewSaving || !canEditClaims ||
      !(isSuperAdmin || isCrewMaster) || !isClaimPendingMasterReview(masterReviewingClaim)) return
    setIsMasterReviewSaving(true)
    try {
      if (approve) {
        const {crewId, claimMonth} = masterReviewingClaim
        if (!await ensureOvertimeReviewCompleted(crewId, claimMonth, 'send this claim for office review')) return
        if (!await ensurePreviousAllotmentReviewCompleted(crewId, claimMonth, 'send this claim for office review')) return
      }
      await saveCrewClaimMasterReview(masterReviewingClaim.id, masterRemarks.trim(), approve)
      setMasterReviewingClaim(null)
      toast.success(approve ? 'Claim approved and sent for office review' : 'Master review remarks saved')
      await fetchClaims()
    } catch (error) {
      showErrorToast(error, 'Failed to save master review', 'save')
    } finally {
      setIsMasterReviewSaving(false)
    }
  }

  const handleReviewClaim = async (claim: CrewClaim) => {
    setReviewingClaim(claim)
    setIsReviewModalOpen(true)
  }

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false)
    setReviewingClaim(null)
  }

  const handleCloseMasterBulkReviewModal = () => {
    setIsMasterBulkReviewModalOpen(false)
  }

  const handleMasterBulkReviewAll = async (masterRemarks: string, period?: string, file?: File | null) => {
    setIsMasterBulkReviewSaving(true)
    try {
      const vesselId = selectedVesselId ? Number(selectedVesselId) : undefined
      const selectedMonthKey = period || selectedMonth || currentMonthStartLocal()
      await bulkMasterReviewCrewClaims(selectedMonthKey, vesselId, masterRemarks.trim(), file)
      await fetchClaims()
      handleCloseMasterBulkReviewModal()
      toast.success('Master bulk review completed successfully')
    } catch (error: any) {
      console.error('Error bulk master reviewing claims:', error)
      showErrorToast(error, 'Failed to bulk master review claims', 'save')
    } finally {
      setIsMasterBulkReviewSaving(false)
    }
  }

  const upsertClaimInState = (updatedClaim: CrewClaim) => {
    setClaims((prev) => {
      const index = prev.findIndex((item) => item.id === updatedClaim.id)
      if (index < 0) return [updatedClaim, ...prev]
      const next = [...prev]
      next[index] = updatedClaim
      return next
    })
  }

  const handleSaveReviewOnly = async (officeRemarks: string) => {
    if (!reviewingClaim) return
    setIsReviewSaving(true)
    try {
      const updated = await updateCrewClaim(reviewingClaim.id, {
        officeRemarks: officeRemarks.trim(),
      })

      upsertClaimInState(updated)
      setReviewingClaim(updated)
      if (statusFilter === 'SUBMITTED' && updated.status !== 'SUBMITTED') {
        setStatusFilter(updated.status)
      }

      toast.success('Office review saved')
      handleCloseReviewModal()
    } catch (error) {
      console.error('Error saving office review:', error)
      showErrorToast(error, 'Failed to save office review', 'save')
    } finally {
      setIsReviewSaving(false)
    }
  }

  const handleCloseSatisfactory = async (officeRemarks: string) => {
    if (!reviewingClaim) return
    setIsReviewSaving(true)
    try {
      await updateCrewClaim(reviewingClaim.id, {
        officeRemarks: officeRemarks.trim(),
      })

      const reviewed = await reviewCrewClaim(reviewingClaim.id)
      upsertClaimInState(reviewed)
      if (statusFilter === 'SUBMITTED' && reviewed.status === 'REVIEWED') {
        setStatusFilter('REVIEWED')
      }

      toast.success('Claim closed satisfactorily')
      handleCloseReviewModal()
    } catch (error) {
      console.error('Error closing claim satisfactorily:', error)
      showErrorToast(error, 'Failed to close claim satisfactorily', 'save')
    } finally {
      setIsReviewSaving(false)
    }
  }

  const handleRejectClaim = async (officeRemarks: string) => {
    if (!reviewingClaim) return
    setIsReviewSaving(true)
    try {
      await updateCrewClaim(reviewingClaim.id, {
        officeRemarks: officeRemarks.trim(),
      })

      const rejected = await rejectCrewClaim(reviewingClaim.id, officeRemarks.trim())
      upsertClaimInState(rejected)
      if (statusFilter === 'SUBMITTED' && rejected.status === 'REJECTED') {
        setStatusFilter('REJECTED')
      }

      toast.success('Claim rejected')
      handleCloseReviewModal()
    } catch (error) {
      console.error('Error rejecting claim:', error)
      showErrorToast(error, 'Failed to reject claim', 'save')
    } finally {
      setIsReviewSaving(false)
    }
  }

  const handleNewClaim = async () => {
    if (isCrew && !isCrewMaster && hasExistingClaimForCurrentNonMasterCrew) {
      toast.error(
        'You can create only your first claim. From 2nd month onward, Crew Master must create it.'
      )
      return
    }

    const currentMonth = selectedMonth ? `${selectedMonth}-01` : currentMonthStartLocal()
    const currentMonthKey = toMonthKey(currentMonth)
    const defaultCrew = !isCrew || isCrewMaster ? null : selfCrewMember ?? null
    const defaultCrewSignOnMonth = toSignOnMonthKey(defaultCrew?.latestSignOnDate)
    const initialClaimMonthKey =
      defaultCrewSignOnMonth && (!currentMonthKey || defaultCrewSignOnMonth > currentMonthKey)
        ? defaultCrewSignOnMonth
        : currentMonthKey
    const initialClaimMonth = initialClaimMonthKey ? `${initialClaimMonthKey}-01` : currentMonth
    const defaultCrewName = defaultCrew?.name ?? (isCrew && !isCrewMaster ? currentCrewName : '')
    const defaultRankId =
      defaultCrew?.rankId ?? (isCrew && !isCrewMaster ? crewRankId || null : null)
    const defaultRankName =
      (defaultRankId ? ranks.find((rank) => rank.id === defaultRankId)?.rank : undefined) ??
      (isCrew && !isCrewMaster ? (currentUser as any)?.rank?.rank : undefined) ??
      ''

    const defaultCrewId = defaultCrew?.id ?? (isCrew && !isCrewMaster ? currentCrewId : 0)
    let prefetchedSalary = 0
    let prefetchedAccountCurrency: string | null = null
    let prefetchedOtherCurrency: string | null = null
    let prefetchedBfAmount = 0
    if (defaultCrewId && defaultCrewId > 0) {
      try {
        const [latestBanking, allotments] = await Promise.all([
          getCrewLatestBankingDetails(defaultCrewId),
          getCrewAllotments(defaultCrewId).catch(() => []),
        ])
        const numericSalary = Number(latestBanking?.salary ?? 0)
        prefetchedSalary = Number.isFinite(numericSalary) ? numericSalary : 0
        prefetchedAccountCurrency = (latestBanking?.salaryCurrency ?? null) as string | null
        prefetchedOtherCurrency = latestBanking?.salaryOtherCurrency ?? null

        prefetchedBfAmount = resolvePreviousMonthCarryForward(allotments, initialClaimMonth, claims, defaultCrewId)

        const resolvedCurrency = resolveCurrencyCode(
          prefetchedAccountCurrency,
          prefetchedOtherCurrency
        )
        if (resolvedCurrency) {
          setCurrencyByCrewId((prev) => ({
            ...prev,
            [defaultCrewId]: resolvedCurrency,
          }))
        }
      } catch (error) {
        console.error('Error prefetching crew salary for new claim:', error)
        prefetchedSalary = 0
      }
    }

    const newClaim: CrewClaim = {
      id: 0,
      srNo: claims.length + 1,
      crewId: defaultCrewId,
      crewName: defaultCrewName,
      vesselId:
        defaultCrew?.vesselId ??
        defaultCrew?.vessel?.id ??
        (isCrew && !isCrewMaster ? currentUser?.vessel?.id ?? null : null),
      vesselName:
        defaultCrew?.vessel?.fleet_name ??
        defaultCrew?.vesselName ??
        (isCrew && !isCrewMaster ? currentUser?.vessel?.fleet_name ?? null : null),
      rankId: defaultRankId,
      rankName: defaultRankName,
      claimMonth: initialClaimMonth,
      salary: prefetchedSalary,
      medical: 0,
      safetyGear: 0,
      travel: 0,
      boardingLodging: 0,
      hraAllowance: 0,
      bonus: 0,
      miscReimbursements: 0,
      overtimeAmount: 0,
      totalAmount: 0,
      bfAmount: prefetchedBfAmount,
      wagesEarned: 0,
      allotmentAmount: 0,
      cashAdvance: 0,
      balanceCf: 0,
      unionSocialDues: 'As per CBA',
      deductionCashAdvanceFromOffice: 0,
      deductionOtherDeductions: 0,
      deductionInternetCard: 0,
      deductionsTotal: 0,
      remarks: '',
      officeRemarks: null,
      status: 'DRAFT',
      submittedAt: null,
      reviewedAt: null,
      reviewedByName: null,
      rejectionReason: null,
      salaryCurrency: prefetchedAccountCurrency,
      salaryOtherCurrency: prefetchedOtherCurrency,
      attachments: [],
      attachmentCount: 0,
      createdAt: '',
      updatedAt: '',
    }
    handleOpenModal(newClaim, true)
  }

  const handleAttachmentAdd = async (files: FileList | null) => {
    if (!editingClaim || !files || editingClaim.id === 0) return

    const fileArray = Array.from(files)
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i]
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File "${file.name}" exceeds 10MB limit`)
        continue
      }
      try {
        const updatedClaim = await uploadCrewClaimAttachment(editingClaim.id, file)
        setEditingClaim(updatedClaim)
        if (attachmentsModalClaim?.id === updatedClaim.id) {
          setAttachmentsModalClaim(updatedClaim)
        }
        await fetchClaims()
        toast.success(`Attachment "${file.name}" uploaded successfully`)
      } catch (error) {
        console.error('Error uploading attachment:', error)
        showErrorToast(error, `Failed to upload ${file.name}`, 'save')
      }
    }
  }

  const handleAttachmentRemove = async (attachmentId: number) => {
    if (!editingClaim || editingClaim.id === 0) return

    try {
      const updatedClaim = await deleteCrewClaimAttachment(editingClaim.id, attachmentId)
      setEditingClaim(updatedClaim)
      if (attachmentsModalClaim?.id === updatedClaim.id) {
        setAttachmentsModalClaim(updatedClaim)
      }
      await fetchClaims()
      toast.success('Attachment removed successfully')
    } catch (error) {
      console.error('Error removing attachment:', error)
      showErrorToast(error, 'Failed to remove attachment', 'delete')
    }
  }

  const deleteClaimNow = async (claim: CrewClaim) => {
    try {
      await deleteCrewClaim(claim.id)
      toast.success('Claim deleted successfully')
      await fetchClaims()
    } catch (error) {
      console.error('Error deleting claim:', error)
      showErrorToast(error, 'Failed to delete claim', 'delete')
    }
  }

  const handleDeleteDraft = (claim: CrewClaim) => {
    const toastId = `delete-claim-${claim.id}`
    if (toast.isActive(toastId)) return
    toast.info(
      ({closeToast}) => (
        <div>
          <div className='mb-2'>Delete draft claim record for {claim.crewName}?</div>
          <div className='d-flex gap-2'>
            <button
              className='btn btn-sm btn-danger'
              onClick={async () => {
                closeToast?.()
                await deleteClaimNow(claim)
              }}
            >
              Delete
            </button>
            <button className='btn btn-sm btn-light' onClick={() => closeToast?.()}>
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        toastId,
        autoClose: false,
        closeOnClick: false,
      }
    )
  }

  // View modal handlers
  const handleOpenViewModal = (claim: CrewClaim) => {
    setViewClaim(claim)
    setIsViewModalOpen(true)
  }

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false)
    setViewClaim(null)
  }

  // Attachments modal handlers
  const handleOpenAttachmentsModal = (claim: CrewClaim) => {
    setAttachmentsModalClaim(claim)
    setIsAttachmentsModalOpen(true)
  }

  const handleCloseAttachmentsModal = () => {
    setIsAttachmentsModalOpen(false)
    setAttachmentsModalClaim(null)
  }

  const startIndex = sortedClaims.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1
  const endIndex = Math.min(currentPage * rowsPerPage, sortedClaims.length)
  const showCrewRemarksColumn = statusFilter !== 'SUBMITTED'

  // Render status badge
  const renderStatusBadge = (claim: CrewClaim) => {
    const isPendingMasterReview =
      !isMasterEquivalentRank(claim.rankId, claim.rankName) &&
      (claim.status === 'MASTER_REVIEW_PENDING' ||
        (claim.status === 'DRAFT' && !!claim.submittedAt))

    if (isPendingMasterReview) {
      return <span className='badge badge-light-warning fw-bold'>Pending Master Review</span>
    }

    const status = claim.status
    switch (status) {
      case 'DRAFT':
        return <span className='badge badge-light-secondary fw-bold'>Draft</span>
      case 'SUBMITTED':
        return canReview ? (
          <span className='badge badge-light-warning fw-bold'>Ready for Review</span>
        ) : (
          <span className='badge badge-light-warning fw-bold'>Submitted</span>
        )
      case 'REVIEWED':
        return <span className='badge badge-light-success fw-bold'>Reviewed</span>
      case 'REJECTED':
        return <span className='badge badge-light-danger fw-bold'>Rejected</span>
      default:
        return <span className='badge badge-light fw-bold'>{status}</span>
    }
  }

  const canEditClaim = (claim: CrewClaim) =>
    !!canEditClaims &&
    (((canCreate || (canManageOfficeDraft && claim.canManageFinancialDraft === true)) && claim.status === 'DRAFT' && !(isCrew && !isCrewMaster && !!claim.submittedAt)) ||
      (canCreate && claim.status === 'MASTER_REVIEW_PENDING' && (isCrewMaster || isSuperAdmin)) ||
      (canReview && claim.status === 'SUBMITTED'))

  const splitClaimRemarks = (claim: CrewClaim) => {
    const normal = (claim.remarks || '').trim()
    const office = (claim.officeRemarks || '').trim()
    if (office) {
      return {normalRemarks: normal, officeRemarks: office}
    }

    // Backward compatibility for older records where office text was appended in remarks.
    const source = normal
    if (!source) return {normalRemarks: '', officeRemarks: ''}

    const officePattern =
      /\[Office Review[^\]]*\]\s*([\s\S]*?)(?=(?:\n\n\[Office Review[^\]]*\])|$)/g
    const officeChunks: string[] = []
    let match: RegExpExecArray | null = null

    while ((match = officePattern.exec(source)) !== null) {
      const text = (match[1] || '').trim()
      if (text) officeChunks.push(text)
    }

    const normalRemarks = source
      .replace(/\n?\n?\[Office Review[^\]]*\]\s*[\s\S]*?(?=(?:\n\n\[Office Review[^\]]*\])|$)/g, '')
      .trim()

    return {
      normalRemarks,
      officeRemarks: officeChunks.join('\n\n'),
    }
  }

  // Render action buttons
  const renderActions = (claim: CrewClaim) => {
    const isNonMasterCrewSecondOrLaterClaim =
      isCrew &&
      !isCrewMaster &&
      Number(claim.crewId || 0) === currentCrewId &&
      firstClaimIdByCrew.get(Number(claim.crewId || 0)) !== Number(claim.id || 0)

    return (
      <div className='d-flex justify-content-left gap-1'>
        {/* View - Everyone can view */}
        {hasPermission('READ_CREW_CLAIM') && (
        <button
          className='btn btn-icon btn-sm'
          title='View'
          onClick={() => handleOpenViewModal(claim)}
        >
          <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
        </button>
        )}

        {/* Edit */}
        {!isNonMasterCrewSecondOrLaterClaim && canEditClaim(claim) && (
          <button
            className='btn btn-icon btn-sm'
            title='Edit'
            onClick={() => handleOpenModal(claim)}
          >
            <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
          </button>
        )}

        {/* Delete - Only creators can delete DRAFT claims */}
        {!isNonMasterCrewSecondOrLaterClaim &&
          canDeleteClaims &&
          canCreate &&
          claim.status === 'DRAFT' &&
          !(isCrew && !isCrewMaster && !!claim.submittedAt) && (
            <button
              className='btn btn-icon btn-sm'
              title='Delete'
              onClick={() => handleDeleteDraft(claim)}
            >
              <KTSVG path='/media/map/red-cross-icon.svg' className='svg-icon-3' />
            </button>
          )}

        {/* Download PDF */}
        {canDownloadClaim && (
          <button
            className='btn btn-icon btn-sm'
            title='Download PDF'
            onClick={() => handleDownloadClaimPDF(claim)}
          >
            <KTSVG path='/media/icons/duotune/files/fil021.svg' className='svg-icon-3 text-success' />
          </button>
        )}

        {canEditClaims && (isCrewMaster || isSuperAdmin) && isClaimPendingMasterReview(claim) && (
          <button
            type='button'
            className='btn btn-light-warning btn-sm'
            onClick={() => setMasterReviewingClaim(claim)}
            disabled={isMasterReviewSaving}
          >
            Master Review
          </button>
        )}

        {/* Initial submission; master forwarding uses the review popup above. */}
        {!isNonMasterCrewSecondOrLaterClaim &&
        !isClaimPendingMasterReview(claim) &&
        (canManageOfficeDraft ? claim.canManageFinancialDraft === true : canEditClaims && canCreate) && claim.status === 'DRAFT' &&
        !(isCrew && !isCrewMaster && !!claim.submittedAt) ? (
          <button
            type='button'
            className='btn btn-light-primary btn-sm'
            onClick={() => handleSubmitClaim(claim)}
          >
            Submit
          </button>
        ) : null}

        {/* Review - Only reviewers (Company/SubCompany/Operator/SuperAdmin) for SUBMITTED claims */}
        {canReview && claim.status === 'SUBMITTED' && (
          <button
            type='button'
            className='btn btn-light-warning btn-sm'
            onClick={() => handleReviewClaim(claim)}
          >
            Review
          </button>
        )}
      </div>
    )
  }

  // Render pagination
  const renderPagination = () => {
    const pages = []
    const showPages = 5
    let startPage = Math.max(1, currentPage - 2)
    let endPage = Math.min(totalPages, startPage + showPages - 1)

    if (endPage - startPage < showPages - 1) {
      startPage = Math.max(1, endPage - showPages + 1)
    }

    if (startPage > 1) {
      pages.push(
        <li key={1} className='page-item'>
          <button
            className='page-link text-muted'
            style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              padding: '8px 12px',
              fontSize: '14px',
              minWidth: '40px',
              borderRadius: '6px',
            }}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
        </li>
      )

      if (startPage > 2) {
        pages.push(
          <li key='ellipsis1' className='page-item disabled'>
            <span
              className='page-link border-0 text-muted'
              style={{backgroundColor: 'transparent', padding: '4px 8px'}}
            >
              ...
            </span>
          </li>
        )
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button
            className='page-link text-muted'
            style={{
              backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
              border: '1px solid #dee2e6',
              padding: '8px 12px',
              fontSize: '14px',
              minWidth: '40px',
              borderRadius: '6px',
              outline: 'none',
              boxShadow: 'none',
            }}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        </li>
      )
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key='ellipsis2' className='page-item disabled'>
            <span
              className='page-link border-0 text-muted'
              style={{backgroundColor: 'transparent', padding: '4px 8px'}}
            >
              ...
            </span>
          </li>
        )
      }

      pages.push(
        <li key={totalPages} className='page-item'>
          <button
            className='page-link text-muted'
            style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              padding: '8px 12px',
              fontSize: '14px',
              minWidth: '40px',
              borderRadius: '6px',
            }}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      )
    }

    return pages
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            {crewOfficeUpdateAlerts.map((claim) => (
              <div
                key={`claim-office-alert-${claim.id}`}
                className='alert alert-info d-flex align-items-center justify-content-between mx-5 mt-4 mb-0 py-3 px-4'
                role='alert'
              >
                <div className='d-flex align-items-center'>
                  <span className='me-2' style={{fontSize: 18}}>
                    i
                  </span>
                  <span className='text-dark'>
                    Office updated your crew claim for{' '}
                    <strong>
                      {claim.claimMonth
                        ? formatMonthLabel(claim.claimMonth, 'short')
                        : 'selected month'}
                    </strong>
                    .
                  </span>
                </div>
                <button
                  type='button'
                  className='btn-close'
                  aria-label='Close'
                  onClick={() => {
                    const updated = new Set(dismissedOfficeAlertKeys)
                    updated.add(getOfficeAlertKey(claim))
                    setDismissedOfficeAlertKeys(updated)
                    try {
                      sessionStorage.setItem(
                        crewOfficeAlertStorageKey,
                        JSON.stringify(Array.from(updated))
                      )
                    } catch {}
                  }}
                />
              </div>
            ))}

            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Crew Claims and Reimbursement</h3>
                <p className='text-muted mb-0 mt-1'>
                  Submit and review crew claims and reimbursements with salary-earned details.
                </p>
              </div>
              <div className='card-toolbar d-flex gap-2'>
                {canCreate &&
                  !(isCrew && !isCrewMaster && hasExistingClaimForCurrentNonMasterCrew) && (
                    <button
                      type='button'
                      className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                      style={{
                        backgroundColor: 'rgb(29, 78, 216)',
                        borderColor: 'rgb(29, 78, 216)',
                        borderRadius: '9.75px',
                        padding: '8px 13px',
                        fontSize: '11.5px',
                        fontWeight: 700,
                        boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                        transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                        gap: '6.5px',
                      }}
                      onClick={handleNewClaim}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='14'
                        height='14'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        style={{ width: '13px', height: '13px' }}
                      >
                        <line x1='12' y1='5' x2='12' y2='19'></line>
                        <line x1='5' y1='12' x2='19' y2='12'></line>
                      </svg>
                      Add Claim
                    </button>
                  )}
                {(isSuperAdmin || isCrewMaster) && (
                  <button
                    type='button'
                    className='btn fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                    style={{
                      backgroundColor: 'rgb(238, 242, 255)',
                      color: 'rgb(79, 70, 229)',
                      borderColor: 'rgb(224, 231, 255)',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      boxShadow: 'rgba(0, 0, 0, 0.04) 0px 2px 6px',
                      transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                      gap: '6.5px',
                    }}
                    onClick={() => setIsMasterBulkReviewModalOpen(true)}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      style={{ width: '13px', height: '13px' }}
                    >
                      <path d='M18 6L7 17l-5-5'></path>
                      <path d='M22 10l-7.5 7.5L13 16'></path>
                    </svg>
                    Master Bulk Review ({pendingMasterReviewRecords.length})
                  </button>
                )}
                {canExportClaim && (
                  <button
                    type='button'
                    className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                    style={{
                      backgroundColor: 'rgb(220, 38, 38)',
                      borderColor: 'rgb(220, 38, 38)',
                      borderRadius: '9.75px',
                      padding: '8px 13px',
                      fontSize: '11.5px',
                      fontWeight: 700,
                      boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                      transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                      gap: '6.5px',
                    }}
                    onClick={() => setIsExportModalOpen(true)}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      style={{ width: '13px', height: '13px' }}
                    >
                      <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'></path>
                      <polyline points='7 10 12 15 17 10'></polyline>
                      <line x1='12' y1='15' x2='12' y2='3'></line>
                    </svg>
                    Export PDF
                  </button>
                )}
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3'>
                <div className='col-md-3'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    {useCrewDropdownFilter ? 'Crew' : 'Search'}
                  </label>
                  {useCrewDropdownFilter ? (
                    <select
                      className='form-select'
                      value={selectedCrewId}
                      onChange={(event) =>
                        setSelectedCrewId(event.target.value ? Number(event.target.value) : '')
                      }
                    >
                      <option value=''>All Crew</option>
                      {crewFilterOptions.map((crew) => (
                        <option key={crew.id} value={crew.id}>
                          {crew.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Search crew or rank'
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                  )}
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Month / Year
                  </label>
                  <input
                    type='month'
                    className='form-control'
                    value={selectedMonth}
                    onChange={(event) => handleSelectedMonthChange(event.target.value)}
                  />
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Status
                  </label>
                  <select
                    className='form-select'
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value as 'All' | ClaimStatus)}
                  >
                    <option value='All'>All Status</option>
                    {canViewDrafts && <option value='DRAFT'>Draft</option>}
                    {canViewCrewFinancialDrafts({isSuperAdmin, isOfficeRole}) && (
                      <option value='MASTER_REVIEW_PENDING'>Pending Master Review</option>
                    )}
                    <option value='SUBMITTED'>Submitted</option>
                    <option value='REVIEWED'>Reviewed</option>
                    <option value='REJECTED'>Rejected</option>
                  </select>
                </div>

                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Crew Status
                  </label>
                  <select
                    className='form-select'
                    value={crewStatusFilter}
                    onChange={(event) => setCrewStatusFilter(event.target.value as 'ALL' | 'SIGNED_ON' | 'SIGNED_OFF')}
                  >
                    <option value='ALL'>All</option>
                    <option value='SIGNED_ON'>Signed On</option>
                    <option value='SIGNED_OFF'>Signed Off</option>
                  </select>
                </div>
              </div>

            </div>



            {/* Summary Cards */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <div className='border rounded p-3'>
                    <div className='text-muted fs-7 fw-semibold'>Total Claims</div>
                    <div className='fs-3 fw-bold text-dark'>{totals.totalClaims}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='border rounded p-3'>
                    <div className='text-muted fs-7 fw-semibold'>Submitted</div>
                    <div className='fs-3 fw-bold text-warning'>{totals.submitted}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='border rounded p-3'>
                    <div className='text-muted fs-7 fw-semibold'>Reviewed</div>
                    <div className='fs-3 fw-bold text-success'>{totals.reviewed}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='border rounded p-3'>
                    <div className='text-muted fs-7 fw-semibold'>Total Amount</div>
                    <div className='fs-3 fw-bold text-primary'>
                      {formatCurrency(totals.totalAmount)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='report-table table-responsive'>
                <div style={{overflowX: 'auto'}} onWheel={handleTableHorizontalWheelScroll}>
                  <table className='table table-bordered align-middle'>
                      <thead className='table-header text-start'>
                        <tr>
                          <th className='text-center text-nowrap' style={{minWidth: '70px'}}>
                            Sr/No
                          </th>
                          <th style={{minWidth: '180px'}}>
                            <div className='d-flex align-items-center text-nowrap'>
                              <span className='me-1'>Name</span>
                              <button
                                type='button'
                                className='btn btn-link p-0 m-0 pb-1'
                                onClick={() => handleSort('crewName')}
                              >
                                <KTSVG
                                  path={`/media/map/sort-col-${
                                    sortConfig.key === 'crewName'
                                      ? sortConfig.direction === 'asc'
                                        ? 'up-black'
                                        : 'down-black'
                                      : 'grey'
                                  }.svg`}
                                  className='svg-icon-3'
                                />
                              </button>
                            </div>
                          </th>
                          <th style={{minWidth: '150px'}}>
                            <div className='d-flex align-items-center text-nowrap'>
                              <span className='me-1'>Rank</span>
                              <button
                                type='button'
                                className='btn btn-link p-0 m-0 pb-1'
                                onClick={() => handleSort('rankName')}
                              >
                                <KTSVG
                                  path={`/media/map/sort-col-${
                                    sortConfig.key === 'rankName'
                                      ? sortConfig.direction === 'asc'
                                        ? 'up-black'
                                        : 'down-black'
                                      : 'grey'
                                  }.svg`}
                                  className='svg-icon-3'
                                />
                              </button>
                            </div>
                          </th>
                          <th style={{minWidth: '130px'}} className='text-nowrap'>
                            Month
                          </th>
                          <th style={{minWidth: '120px'}} className='text-nowrap'>
                            Salary
                          </th>
                          <th style={{minWidth: '120px'}} className='text-nowrap'>
                            Medical
                          </th>
                          <th style={{minWidth: '120px'}} className='text-nowrap'>
                            Travel
                          </th>
                          <th style={{minWidth: '160px'}} className='text-nowrap'>
                            Boarding & Lodging
                          </th>
                          <th style={{minWidth: '140px'}} className='text-nowrap'>
                            HRA Allowance
                          </th>
                          <th style={{minWidth: '120px'}} className='text-nowrap'>
                            Bonus
                          </th>
                          <th style={{minWidth: '160px'}} className='text-nowrap'>
                            Misc. Reimbursements
                          </th>
                          <th style={{minWidth: '150px'}} className='text-nowrap'>
                            Overtime Amount
                          </th>
                          <th style={{minWidth: '140px', backgroundColor: '#F4F9FF'}}>
                            <div className='d-flex align-items-center text-nowrap'>
                              <span className='me-1'>Total Claim</span>
                              <button
                                type='button'
                                className='btn btn-link p-0 m-0 pb-1'
                                onClick={() => handleSort('total')}
                              >
                                <KTSVG
                                  path={`/media/map/sort-col-${
                                    sortConfig.key === 'total'
                                      ? sortConfig.direction === 'asc'
                                        ? 'up-black'
                                        : 'down-black'
                                      : 'grey'
                                  }.svg`}
                                  className='svg-icon-3'
                                />
                              </button>
                            </div>
                          </th>
                          <th style={{minWidth: '140px'}} className='text-nowrap'>
                            B/F Amount
                          </th>
                          <th style={{minWidth: '140px'}} className='text-nowrap'>
                            Wages Earned
                          </th>
                          <th style={{minWidth: '140px'}} className='text-nowrap'>
                            Crew Deduction
                          </th>
                          <th style={{minWidth: '140px'}} className='text-nowrap'>
                            Balance Wages
                          </th>
                          <th style={{minWidth: '120px'}} className='text-nowrap'>
                            Attachments
                          </th>
                          {showCrewRemarksColumn && (
                            <th style={{minWidth: '160px', maxWidth: '220px'}} className='text-nowrap'>
                              Remarks
                            </th>
                          )}
                          <th style={{minWidth: '160px', maxWidth: '220px'}} className='text-nowrap'>
                            Office Remarks
                          </th>
                          <th style={{minWidth: '120px'}}>
                            <div className='d-flex align-items-center text-nowrap'>
                              <span className='me-1'>Status</span>
                              <button
                                type='button'
                                className='btn btn-link p-0 m-0 pb-1'
                                onClick={() => handleSort('status')}
                              >
                                <KTSVG
                                  path={`/media/map/sort-col-${
                                    sortConfig.key === 'status'
                                      ? sortConfig.direction === 'asc'
                                        ? 'up-black'
                                        : 'down-black'
                                      : 'grey'
                                  }.svg`}
                                  className='svg-icon-3'
                                />
                              </button>
                            </div>
                          </th>
                          <th
                            style={{minWidth: '170px', textAlign: 'center'}}
                            className='text-nowrap'
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className='table-body text-start'>
                        {isLoading ? (
                          <TableSkeleton rows={rowsPerPage || 10} cols={showCrewRemarksColumn ? 22 : 21} />
                        ) : sortedClaims.length === 0 ? (
                          <tr>
                            <td
                              colSpan={showCrewRemarksColumn ? 22 : 21}
                              className='text-center text-muted py-5'
                            >
                              No claims found for the selected filters.
                            </td>
                          </tr>
                        ) : (
                          currentRecords.map((claim, idx) => {
                            const {normalRemarks, officeRemarks} = splitClaimRemarks(claim)
                            return (
                              <tr key={claim.id}>
                                <td className='text-center'>{indexOfFirstRecord + idx + 1}</td>
                                <td>{claim.crewName}</td>
                                <td>{claim.rankName || '—'}</td>
                                <td>{formatClaimMonthDisplay(claim.claimMonth)}</td>
                                <td>{formatClaimAmount(claim, claim.salary || 0)}</td>
                                <td>{formatClaimAmount(claim, claim.medical || 0)}</td>
                                <td>{formatClaimAmount(claim, claim.travel || 0)}</td>
                                <td>{formatClaimAmount(claim, claim.boardingLodging || 0)}</td>
                                <td>{formatClaimAmount(claim, claim.hraAllowance || 0)}</td>
                                <td>{formatClaimAmount(claim, claim.bonus || 0)}</td>
                                <td>{formatClaimAmount(claim, claim.miscReimbursements || 0)}</td>
                                <td>{formatClaimAmount(claim, claim.overtimeAmount || 0)}</td>
                                <td className='fw-bold' style={{backgroundColor: '#F4F9FF'}}>
                                  {formatClaimAmount(claim, calculateRowTotal(claim))}
                                </td>
                                <td>{formatOptionalClaimAmount(claim, claim.bfAmount)}</td>
                                <td>{formatOptionalClaimAmount(claim, claim.wagesEarned)}</td>
                                <td>
                                  {formatOptionalClaimAmount(claim, getCrewDeductionValue(claim))}
                                </td>
                                <td>
                                  {formatOptionalClaimAmount(claim, getBalanceWagesValue(claim))}
                                </td>
                                <td>
                                  {(claim.attachmentCount || 0) > 0 ? (
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-light-primary text-nowrap'
                                      onClick={() => handleOpenAttachmentsModal(claim)}
                                    >
                                      View Files ({claim.attachmentCount || 0})
                                    </button>
                                  ) : (
                                    <span className='text-muted'>No attachments</span>
                                  )}
                                </td>
                                {showCrewRemarksColumn && (
                                  <td>
                                    <RemarksCell
                                      text={normalRemarks}
                                      title='Crew Claim Remarks'
                                      subtitle={claim.crewName || ''}
                                      onOpenModal={(title, subtitle, text) => {
                                        setRemarksModal({ visible: true, title, subtitle, text })
                                      }}
                                    />
                                  </td>
                                )}
                                <td>
                                  <RemarksCell
                                    text={officeRemarks}
                                    title='Office Remarks'
                                    subtitle={claim.crewName || ''}
                                    onOpenModal={(title, subtitle, text) => {
                                      setRemarksModal({ visible: true, title, subtitle, text })
                                    }}
                                  />
                                </td>
                                <td className='text-nowrap'>{renderStatusBadge(claim)}</td>
                                <td className='text-nowrap'>{renderActions(claim)}</td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            colSpan={showCrewRemarksColumn ? 21 : 20}
                            className='text-end fw-bold text-dark'
                          >
                            Total Claim Amount
                          </td>
                          <td className='fw-bold text-dark text-center'>
                            {formatCurrency(totals.totalAmount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                </div>

                {/* Pagination */}
                <div
                  className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                  style={{
                    position: 'static',
                    bottom: 0,
                    backgroundColor: '#fff',
                    zIndex: 10,
                  }}
                >
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <select
                      className='form-select'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px',
                      }}
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{fontSize: '14px'}}>
                      Showing{' '}
                      <strong>
                        {startIndex}-{endIndex}
                      </strong>{' '}
                      of <strong>{sortedClaims.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>
                        {renderPagination()}
                        <li
                          className={`page-item ${
                            currentPage === totalPages || totalPages === 0 ? 'disabled' : ''
                          }`}
                        >
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crew Claim Modal */}
      <CrewClaimModal
        isOfficeEntry={isOfficeRole && !isSuperAdmin}
        showCompanyFilter={showCompanyFilter}
        showSubCompanyFilter={showSubCompanyFilter && !fixedCompanyAdminId}
        isOpen={isModalOpen}
        canCreate={canCreate}
        canEditDraft={!!editingClaim && canEditClaim(editingClaim)}
        canReview={canReview}
        isNewClaim={isNewClaim}
        restrictToMedicalTravelOnFirstFill={isCrew && !isCrewMaster}
        showOneTimeClaimFields={showOneTimeClaimFields}
        lockClaimAmountInputs={hasExistingClaimForSelectedMonth}
        canEditMasterPending={isCrewMaster || isSuperAdmin}
        canEditSubmittedByOffice={canReview}
        lockCrewEnteredCoreFieldsForMaster={
          isCrewMaster &&
          !!editingClaim &&
          !isMasterEquivalentRank(editingClaim.rankId, editingClaim.rankName) &&
          (editingClaim.status === 'MASTER_REVIEW_PENDING' ||
            (editingClaim.status === 'DRAFT' && !!editingClaim.submittedAt))
        }
        lockCrewSelection={isCrew && !isCrewMaster}
        claim={editingClaim}
        ranks={ranks}
        crewMembers={(isSuperAdmin || canManageOfficeDraft) && isNewClaim ? modalCrewMembers : selectableCrewMembers}
        companies={companies}
        companyAdmins={companyAdmins}
        vessels={vessels}
        selectedCompanyGroupId={fixedCompanyGroupId ?? modalCompanyGroupId}
        selectedCompanyAdminId={fixedCompanyAdminId ?? modalCompanyAdminId}
        selectedVesselId={modalVesselId}
        onCompanyGroupChange={(next) => {
          setModalCompanyGroupId(next)
          setModalCompanyAdminId('')
          setModalVesselId('')
        }}
        onCompanyAdminChange={(next) => {
          setModalCompanyAdminId(next)
          setModalVesselId('')
        }}
        onVesselChange={(next) => setModalVesselId(next)}
        onClose={handleCloseModal}
        onSave={handleSaveClaim}
        onChange={(nextClaim) => setEditingClaim(nextClaim)}
        onAttachmentAdd={handleAttachmentAdd}
        onAttachmentRemove={handleAttachmentRemove}
        calculateRowTotal={calculateRowTotal}
        formatCurrency={formatCurrency}
        isLoading={isSaving}
        crewSignOnDateById={(isSuperAdmin || canManageOfficeDraft) && isNewClaim ? modalSignOnDateByCrewId : crewSignOnDateByCrewId}
      />



      {/* View Claim Modal */}
      <ViewCrewClaimModal
        visible={isViewModalOpen}
        claim={viewClaim}
        onClose={handleCloseViewModal}
        onViewAttachments={() => {
          if (viewClaim) {
            handleCloseViewModal()
            handleOpenAttachmentsModal(viewClaim)
          }
        }}
      />

      {/* Review Claim Modal */}
      <CrewClaimMasterReviewModal
        visible={!!masterReviewingClaim}
        record={masterReviewingClaim}
        onClose={() => { if (!isMasterReviewSaving) setMasterReviewingClaim(null) }}
        onSave={(remarks) => handleMasterReviewClaim(remarks, false)}
        onApprove={(remarks) => handleMasterReviewClaim(remarks, true)}
        isLoading={isMasterReviewSaving}
      />

      <CrewClaimReviewModal
        visible={isReviewModalOpen}
        claim={reviewingClaim}
        onClose={handleCloseReviewModal}
        onSave={handleSaveReviewOnly}
        onCloseSatisfactory={handleCloseSatisfactory}
        onReject={handleRejectClaim}
        isLoading={isReviewSaving}
      />

      {/* Attachments Modal */}
      <CrewClaimAttachmentsModal
        visible={isAttachmentsModalOpen}
        title={`Attachments - ${attachmentsModalClaim?.crewName || 'Claim'}`}
        subtitle={attachmentsModalClaim?.rankName || null}
        loading={false}
        items={(attachmentsModalClaim?.attachments || []).map((att) => ({
          id: att.id,
          fileName: att.fileName,
          viewUrl: crewClaimAttachmentViewUrl(attachmentsModalClaim!.id, att.id),
          downloadUrl: crewClaimAttachmentDownloadUrl(attachmentsModalClaim!.id, att.id),
          fileType: att.fileType,
          fileSize: att.fileSize,
        }))}
        onClose={handleCloseAttachmentsModal}
        onViewFile={(item) => {
          setFileModal({
            visible: true,
            title: item.fileName,
            fileName: item.fileName,
            viewUrl: item.viewUrl,
            downloadUrl: item.downloadUrl,
          })
        }}
      />

      {/* File Viewer Modal */}
      <FileViewerModal
        visible={fileModal.visible}
        title={fileModal.title}
        fileName={fileModal.fileName}
        viewUrl={fileModal.viewUrl}
        downloadUrl={fileModal.downloadUrl}
        onClose={() => setFileModal((prev) => ({...prev, visible: false}))}
      />

      {/* Export Modal */}
      <CrewClaimMasterBulkReviewModal
        visible={isMasterBulkReviewModalOpen}
        records={pendingMasterReviewRecords}
        period={
          selectedMonth
            ? selectedMonth
            : pendingMasterReviewRecords[0]?.claimMonth
            ? pendingMasterReviewRecords[0].claimMonth.slice(0, 7)
            : 'Selected Month'
        }
        onClose={handleCloseMasterBulkReviewModal}
        onReviewAll={handleMasterBulkReviewAll}
        isLoading={isMasterBulkReviewSaving}
      />

      <CrewClaimExportModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        initialFilters={{
          companyGroupId: selectedCompanyGroupId ? Number(selectedCompanyGroupId) : null,
          companyAdminId: selectedCompanyAdminId ? Number(selectedCompanyAdminId) : null,
          vesselId: selectedVesselId ? Number(selectedVesselId) : null,
          crewId: selectedCrewId ? Number(selectedCrewId) : null,
          month: selectedMonth || null,
        }}
        currentUser={currentUser}
        isSuperAdmin={isSuperAdmin}
        isCompanyAdmin={isCompanyAdmin}
        isCompanyGroupAdmin={isCompanyGroupAdmin}
        isOperator={isOperator}
        isCrewMaster={isCrewMaster}
        isCrew={isCrew}
        vessels={vessels}
      />

      <RemarksModal
        visible={remarksModal.visible}
        title={remarksModal.title}
        subtitle={remarksModal.subtitle}
        remarksText={remarksModal.text}
        onClose={() => setRemarksModal(prev => ({ ...prev, visible: false }))}
      />

      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{top: '5rem'}}
      />
    </div>
  )
}

export default CrewClaims
